import { useLingui } from '@lingui/react/macro';
import { AlertDialog, Button, Input, toast } from '@utima/ui';
import { useCallback, useState, type MouseEvent, useEffect } from 'react';

type ConfirmDialogProps = {
  onConfirm?: () => Promise<unknown> | unknown;
  onCancel?: () => Promise<unknown> | unknown;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  description?: string;
  confirmationText?: string;
  confirmationLabel?: string;
};

export function ConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  onCancel,
  description,
  confirmationText,
  confirmationLabel,
}: ConfirmDialogProps) {
  const { t } = useLingui();
  const [confirming, setConfirming] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [typedText, setTypedText] = useState('');

  useEffect(() => {
    if (!open) {
      setTypedText('');
    }
  }, [open]);

  const isConfirmEnabled = confirmationText
    ? typedText.trim() === confirmationText.trim()
    : true;

  const handleConfirm = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (!isConfirmEnabled) {
        return;
      }

      try {
        setConfirming(true);
        await onConfirm?.();

        // Show success notification
        toast.success(t`Success`, {
          description: t`Item was successfully deleted.`,
        });
        onOpenChange(false);
      } finally {
        setConfirming(false);
      }
    },
    [onConfirm, t, onOpenChange, isConfirmEnabled],
  );

  const handleCancel = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      try {
        setCanceling(true);
        await onCancel?.();
        onOpenChange(false);
      } finally {
        setCanceling(false);
      }
    },
    [onCancel, onOpenChange],
  );

  return (
    <AlertDialog.Root open={open}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay />
        <AlertDialog.Content>
          <AlertDialog.Header>
            <AlertDialog.Title>{t`Delete?`}</AlertDialog.Title>
            <AlertDialog.Description>
              {description ?? t`Are you sure you want to delete this item?`}
            </AlertDialog.Description>
          </AlertDialog.Header>

          {confirmationText && (
            <div className='space-y-2'>
              <label className='text-sm font-medium'>
                {confirmationLabel ?? t`Type '${confirmationText}' to confirm:`}
              </label>
              <Input
                value={typedText}
                onChange={e => setTypedText(e.target.value)}
                onPaste={e => e.preventDefault()}
                placeholder={confirmationText}
                disabled={confirming || canceling}
                autoComplete='off'
              />
            </div>
          )}

          <AlertDialog.Footer>
            <AlertDialog.Cancel asChild>
              <Button
                onClick={handleCancel}
                loading={canceling}
                disabled={canceling || confirming}
                variant='ghost'
              >
                {t`Cancel`}
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action asChild>
              <Button
                variant='danger'
                onClick={handleConfirm}
                loading={confirming}
                disabled={canceling || confirming || !isConfirmEnabled}
              >
                {t`Confirm`}
              </Button>
            </AlertDialog.Action>
          </AlertDialog.Footer>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
